import React, { useEffect } from 'react';
import { useIsMount } from '../../../../../hooks/useIsMountHook';
import _ from 'lodash';
import './Waves.styles.css';
import { DeploymentStageStatus } from '../VirtualServiceBootingScreen/bootingScreenUtils';

const PENDING_WAVE_COLOR = ['#D33F4C', '#D33F4C'];
const BOOTING_WAVE_COLOR = ['#89CAF8', '#89CAF8'];
const STARTED_WAVE_COLOR = ['#8952FF', '#8952FF'];
const DOWNLOADING_WAVE_COLOR = ['#F2AA00', '#F6C03D'];
const READY_WAVE_COLOR = ['#16AF10', '#41C53C'];
const WAVE_WIDTH = 150;

export const WAVE_MARGIN = 65;
export const INITIAL_WAVE_HEIGHT = 20;

const WAVE_SWING_UP_ACTION_MAX_HEIGHT = 40;
const WAVE_SWING_DOWN_ACTION_MIN_HEIGHT = 5;
export const WAVE_LOADER_SWING_INTERVAL = 2500;

// this is how we control wave swing up and down timing
const swingUpActionTimeout = waveIndex => waveIndex * 150;
const swingDownActionTimeout = waveIndex => waveIndex * 150 + 500;

export interface IBootingScreenStatuses {
  pending: number;
  booting: number;
  containerReady: number;
  started: number;
}

export type WaveTypes = keyof IBootingScreenStatuses;

export type WaveSettings = {
  type: WaveTypes;
  height: number;
  loc: number;
  style: string;
};

export type WaveElement = {
  path: SVGPathElement;
  text: SVGTextElement;
  percentText: SVGTextElement;
  percent?: SVGTextElement;
  textGroup?: SVGGElement;
  translateGroup: string;
};

export function preparePaths(
  wavPaths: Omit<WaveSettings, 'loc' | 'height'>[],
  initialWaveHeight: number,
  waveMargin: number,
): WaveSettings[] {
  return wavPaths.map((path, index) => {
    return {
      ...path,
      height: initialWaveHeight,
      loc: index * waveMargin,
    };
  });
}

const types: Omit<WaveSettings, 'loc' | 'height'>[] = [
  {
    type: 'pending',
    style: 'fill: url("#gradient-1574685867250732")',
  },
  {
    type: 'booting',
    style: 'fill: url("#gradient-1574685867251734")',
  },
  {
    type: 'containerReady',
    style: 'fill: url("#gradient-1574685867252736")',
  },
  {
    type: 'started',
    style: 'fill: url("#gradient-1574685867253738")',
  },
];

/**
 * swing-state - in this state the loader is swinging (going up & down). no progress is shown.
 * default-state - usually comes after the 'swing-state'. shows progress info with small swings.
 */
export type WaveLoaderState = 'default-state' | 'swing-state';

export const paths: WaveSettings[] = preparePaths(types, INITIAL_WAVE_HEIGHT, WAVE_MARGIN);

interface WavesProps {
  status: DeploymentStageStatus;
}

/**/
const createInitialState = (status: DeploymentStageStatus) => {
  switch (status) {
    case 'STARTING':
    case 'GENERATING_DATA':
      return { pending: 100, containerReady: 0, started: 0, booting: 0 };
    case 'STARTING_CONTAINER':
    case 'WAITING_FOR_DOMAIN':
      return { pending: 0, containerReady: 0, started: 0, booting: 100 };
    case 'CONTAINER_READY':
    case 'INDEXING_DATA':
      return { pending: 0, containerReady: 100, started: 0, booting: 0 };
    case 'FINISHED':
      return { pending: 0, containerReady: 0, started: 100, booting: 0 };
    default:
      return { pending: 100, containerReady: 0, started: 0, booting: 0 };
  }
};

const Waves = (props: WavesProps) => {
  const waveRef = React.createRef<SVGGElement>();
  const isMount = useIsMount();

  const [elements, setElements] = React.useState(null);
  const [swingLoaderInterval, setSwingLoaderInterval] = React.useState(null);
  const [defaultLoaderInterval, setDefaultLoaderInterval] = React.useState(null);
  const [statuses, setStatuses] = React.useState<IBootingScreenStatuses>(createInitialState(props.status));

  useEffect(() => {
    if (!isMount) {
      switch (props.status) {
        case 'STARTING':
          setStatuses({ pending: 100, containerReady: 0, started: 0, booting: 0 });
          break;
        case 'STARTING_CONTAINER':
          setStatuses({ pending: 0, containerReady: 0, started: 0, booting: 100 });
          break;
        case 'CONTAINER_READY':
          setStatuses({ pending: 0, containerReady: 100, started: 0, booting: 0 });
          break;
        case 'FINISHED':
          setStatuses({ pending: 0, containerReady: 0, started: 100, booting: 0 });
          break;
      }
    }
  }, [props.status]);

  useEffect(() => {
    if (isMount) {
      createWaveElements();
    }
  }, []);

  useEffect(() => {
    if (!_.isNil(elements)) {
      startDefaultLoader();
    }
  }, [elements]);

  useEffect(() => {
    if (!isMount) {
      clearInterval(defaultLoaderInterval);
      setDefaultLoaderInterval(null);
      startDefaultLoader();
    }
  }, [statuses]);

  const plotOnBell = (x: number, scale?: boolean) => {
    const stdD = 0.125;
    const mean = 0.5;

    if (scale) {
      return (
        1 /
        ((1 / (stdD * Math.sqrt(Math.PI * 2))) *
          Math.pow(Math.E, (Math.pow(x - mean, 2) * -1) / (Math.pow(stdD, 2) * 2)))
      );
    }

    return (
      (1 / (stdD * Math.sqrt(Math.PI * 2))) *
      Math.pow(Math.E, (Math.pow(x - mean, 2) * -1) / (Math.pow(stdD, 2) * 2)) *
      plotOnBell(0.5, true)
    );
  };

  const appendWaves = (waveElements: WaveElement[], waveRef: Element): void => {
    waveElements.map(element => {
      const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      g.setAttribute('transform', element.translateGroup);

      if (element.textGroup) {
        g.appendChild(element.textGroup);
      }
      g.appendChild(element.path);
      waveRef.appendChild(g);
    });
  };

  const createWaveElements = () => {
    const elements = createWaves(paths);
    appendWaves(elements, waveRef.current);
    setElements(elements);
  };

  const getPoints = (base: number, height: number) => {
    const step = 0.5;
    const limit = WAVE_WIDTH;
    const points: [number, number][] = [];

    for (let i = step; i < limit; i += step) {
      const plot = plotOnBell(i / limit);
      points.push([i, base - height * plot]);
    }
    return getPointAttributes(points);
  };

  const getPointAttributes = (points: [number, number][]): string => {
    return 'M 0,50 ' + points.join(' ') + ' 100,50 z';
  };

  const getWaveText = (current: WaveTypes) => {
    switch (current) {
      case 'pending':
        return 'Initializing';
      case 'booting':
        return 'Starting container';
      case 'containerReady':
        return 'Container Ready';
      case 'started':
        return 'Ready';
    }
  };

  const generateWave = (pointsPath: string, waveSettings: WaveSettings): WaveElement => {
    const translateGroup = `translate(${waveSettings.loc}, 20)`;

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('id', waveSettings.type);
    path.setAttribute('style', waveSettings.style);
    path.setAttribute('d', pointsPath);

    const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    text.setAttribute('class', 'status-label');
    text.textContent = getWaveText(waveSettings.type);
    text.setAttribute('text-anchor', 'middle');
    text.setAttribute('alignment-baseline', 'middle');

    const percentText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    percentText.setAttribute('class', 'percent-label');
    percentText.textContent = '0%';
    percentText.setAttribute('text-anchor', 'middle');
    percentText.setAttribute('alignment-baseline', 'middle');

    const textGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    textGroup.setAttribute('class', 'text-group');
    textGroup.appendChild(text);
    textGroup.appendChild(percentText);

    return { path, text, percentText, translateGroup, textGroup };
  };

  const createWaves = (waveSettings: WaveSettings[]): WaveElement[] => {
    return _.map(waveSettings, pathSettings => generateWave(getPoints(30, pathSettings.height), pathSettings));
  };

  const updateWave = (waveElement: WaveElement, progressValue: number, progressText: string) => {
    const adjustValueForSVG = progressValue / 2;
    const waveHeight = adjustValueForSVG < 5 ? 5 : adjustValueForSVG;
    const textLabelX = 75;
    const textLabelY = -waveHeight + 20;

    // set wave path
    waveElement.path.setAttribute('d', getPoints(30, waveHeight));

    // set percents text value
    waveElement.percentText.textContent = progressText;

    // set percent y location
    waveElement.percentText.setAttribute('y', '-7');

    // set texts group y location
    if (waveElement.textGroup) {
      waveElement.textGroup.setAttribute('transform', `translate(${textLabelX}, ${textLabelY})`);
    }
  };

  const generateSwingLoaderWaves = (waveElements: WaveElement[]) => {
    const timeouts: any[] = [];

    for (let i = 0; i < waveElements.length; i = i + 1) {
      timeouts[i] = setTimeout(() => {
        updateWave(waveElements[i], WAVE_SWING_UP_ACTION_MAX_HEIGHT, '');
      }, swingUpActionTimeout(i));

      timeouts[i] = setTimeout(() => {
        updateWave(waveElements[i], WAVE_SWING_DOWN_ACTION_MIN_HEIGHT, '');
      }, swingDownActionTimeout(i));
    }

    return timeouts;
  };

  const startSwingLoader = () => {
    const interval = setInterval(() => {
      generateSwingLoaderWaves(elements);
    }, WAVE_LOADER_SWING_INTERVAL);
    setSwingLoaderInterval(Number(interval));
  };

  const stopSwingLoader = () => {
    clearInterval(swingLoaderInterval);
    setSwingLoaderInterval(null);
  };

  const getStatusesTexts = (statuses: IBootingScreenStatuses) => {
    const hasStatusesData = _.some(statuses, status => status > 0);
    if (!hasStatusesData) {
      return paths.map(() => '');
    }

    let nextValue = -1;

    for (let i = 0; i < paths.length; i++) {
      const waveValue = statuses[paths[i].type];
      if (nextValue < 0 && waveValue > 0) {
        nextValue = i;
      }
    }

    const statusText = {};
    for (let i = 0; i < paths.length; i++) {
      const wave = statuses[paths[i].type];

      if (i < nextValue) {
        statusText[paths[i].type] = 'Done';
      } else {
        statusText[paths[i].type] = `${parseInt(String(wave), 10)}%`;
      }
    }
    return statusText;
  };

  /**
   * adds a variable value between 10-17 to provided argument in order to make wave small swing look better.
   * bigger waves get smaller values.
   *
   * @example
   * smallSwingWaveMultiplier(100) => 110
   * smallSwingWaveMultiplier(50) => 63.5
   * smallSwingWaveMultiplier(0) => 17
   */
  const smallSwingWaveMultiplier = (value: number, base = 7) => value + (1 - value / 100) * base + 10;

  const generateDefaultLoaderWaves = (elements, statuses, statusTexts) => {
    for (let i = 0; i < paths.length; i = i + 1) {
      const path = paths[i];
      const realValue = statuses?.[path?.type] || 0;

      setTimeout(() => {
        updateWave(elements[i], smallSwingWaveMultiplier(realValue), statusTexts[path.type]);
      }, swingUpActionTimeout(i));

      setTimeout(() => {
        updateWave(elements[i], realValue, statusTexts[path.type]);
      }, swingDownActionTimeout(i));

      updateWave(elements[i], realValue, statusTexts[path.type]);
    }
  };

  const startDefaultLoader = () => {
    generateDefaultLoaderWaves(elements, statuses, getStatusesTexts(statuses));
    const interval = setInterval(() => {
      generateDefaultLoaderWaves(elements, statuses, getStatusesTexts(statuses));
    }, WAVE_LOADER_SWING_INTERVAL);

    setDefaultLoaderInterval(Number(interval));
  };

  const stopDefaultLoader = () => {
    clearInterval(defaultLoaderInterval);
    setDefaultLoaderInterval(null);
  };

  const getGradients = () => {
    return (
      <>
        <linearGradient
          xmlns="http://www.w3.org/2000/svg"
          id="gradient-15746858886521297"
          x1="0%"
          y1="50%"
          x2="100%"
          y2="50%"
          vectorEffect="non-scaling-stroke"
        >
          <stop offset="0%" stopColor={PENDING_WAVE_COLOR[0]} stopOpacity="1" vectorEffect="non-scaling-stroke" />
          <stop offset="100%" stopColor={PENDING_WAVE_COLOR[1]} stopOpacity="1" vectorEffect="non-scaling-stroke" />
        </linearGradient>

        <linearGradient
          xmlns="http://www.w3.org/2000/svg"
          id="gradient-1574685867250732"
          x1="0%"
          y1="50%"
          x2="100%"
          y2="50%"
          vectorEffect="non-scaling-stroke"
        >
          <stop offset="0%" stopColor={BOOTING_WAVE_COLOR[0]} stopOpacity="1" vectorEffect="non-scaling-stroke" />
          <stop offset="100%" stopColor={BOOTING_WAVE_COLOR[1]} stopOpacity="1" vectorEffect="non-scaling-stroke" />
        </linearGradient>

        <linearGradient
          xmlns="http://www.w3.org/2000/svg"
          id="gradient-1574685867251734"
          x1="0%"
          y1="50%"
          x2="100%"
          y2="50%"
          vectorEffect="non-scaling-stroke"
        >
          <stop offset="0%" stopColor={STARTED_WAVE_COLOR[0]} stopOpacity="1" vectorEffect="non-scaling-stroke" />
          <stop offset="100%" stopColor={STARTED_WAVE_COLOR[1]} stopOpacity="1" vectorEffect="non-scaling-stroke" />
        </linearGradient>

        <linearGradient
          xmlns="http://www.w3.org/2000/svg"
          id="gradient-1574685867252736"
          x1="0%"
          y1="50%"
          x2="100%"
          y2="50%"
          vectorEffect="non-scaling-stroke"
        >
          <stop offset="0%" stopColor={DOWNLOADING_WAVE_COLOR[0]} stopOpacity="1" vectorEffect="non-scaling-stroke" />
          <stop offset="100%" stopColor={DOWNLOADING_WAVE_COLOR[1]} stopOpacity="1" vectorEffect="non-scaling-stroke" />
        </linearGradient>

        <linearGradient
          xmlns="http://www.w3.org/2000/svg"
          id="gradient-1574685867253738"
          x1="0%"
          y1="50%"
          x2="100%"
          y2="50%"
          vectorEffect="non-scaling-stroke"
        >
          <stop offset="0%" stopColor={READY_WAVE_COLOR[0]} stopOpacity="1" vectorEffect="non-scaling-stroke" />
          <stop offset="100%" stopColor={READY_WAVE_COLOR[1]} stopOpacity="1" vectorEffect="non-scaling-stroke" />
        </linearGradient>
      </>
    );
  };

  return (
    <div className={'wave-loader'}>
      <svg version="1.1" viewBox="0 0 345 80" fill="grey">
        <defs>{getGradients()}</defs>
        <g transform="translate(0, 30)" id="wave" ref={waveRef} />
      </svg>

      {/*Debug stuff*/}
      {/*<div className="text-center mt-4">*/}
      {/*  <Button*/}
      {/*    onClick={() => {*/}
      {/*      startSwingLoader();*/}
      {/*    }}*/}
      {/*  >*/}
      {/*    Animate*/}
      {/*  </Button>*/}
      {/*  <Button*/}
      {/*    onClick={() => {*/}
      {/*      stopSwingLoader();*/}
      {/*    }}*/}
      {/*  >*/}
      {/*    Stop*/}
      {/*  </Button>*/}
      {/*  <Button*/}
      {/*    onClick={() => {*/}
      {/*      startDefaultLoader();*/}
      {/*    }}*/}
      {/*  >*/}
      {/*    Start Default Loader*/}
      {/*  </Button>*/}
      {/*  <Button*/}
      {/*    onClick={() => {*/}
      {/*      stopDefaultLoader();*/}
      {/*    }}*/}
      {/*  >*/}
      {/*    Stop Default Loader*/}
      {/*  </Button>*/}
      {/*  <hr />*/}
      {/*  <Row>*/}
      {/*    <Col md={4} />*/}
      {/*    <Col md={4}>*/}
      {/*      {_.map(statuses, (item, key) => {*/}
      {/*        return (*/}
      {/*          <>*/}
      {/*            <label htmlFor="">{key}</label>*/}
      {/*            <Form.Control*/}
      {/*              min={0}*/}
      {/*              max={100}*/}
      {/*              type={'number'}*/}
      {/*              value={statuses[key]}*/}
      {/*              onChange={e => {*/}
      {/*                const copy = _.cloneDeep(statuses);*/}
      {/*                copy[key] = Number(e.target.value);*/}
      {/*                setStatuses(copy);*/}
      {/*              }}*/}
      {/*            />*/}
      {/*          </>*/}
      {/*        );*/}
      {/*      })}*/}
      {/*    </Col>*/}
      {/*  </Row>*/}
      {/*  <hr />*/}
      {/*  <h1>Statuses</h1>*/}
      {/*  statuses.pending: {statuses.pending} <br />*/}
      {/*  statuses.booting: {statuses.booting} <br />*/}
      {/*  statuses.downloading: {statuses.downloading} <br />*/}
      {/*</div>*/}
    </div>
  );
};

export default Waves;
